import { SfLog } from '@app/adapters/logger'
import SFetch from '@app/utils/sfetch'
import serialize from '../utils/serialize'

const runManualAction = async ({ ledgerIds, action, payload }) => {
  const url = Silverfin.Routes.insight_action_runs_path(Silverfin.currentFirmId)

  try {
    const data = await SFetch.json(url, {
      method: 'POST',
      body: {
        insight_action_run: {
          ledger_ids: ledgerIds,
          action: action,
          payload: payload,
        },
      },
    })

    return data
  } catch (e) {
    SfLog.info('Failed to run a manual trigger action', {
      extra: {
        ...window.Silverfin.jsonParams,
        error: e,
      },
    })

    throw e instanceof Error ? e : new Error(String(e), { cause: e }) // throw to communicate this back to the user in the actions modal
  }
}

const fetchNonActionableCompanies = async ({
  actionName,
  companyIds,
  payload,
}) => {
  const url =
    Silverfin.Routes.non_actionable_companies_insight_action_runs_path(
      Silverfin.currentFirmId
    )

  try {
    const data = await SFetch.json(url, {
      method: 'POST',
      body: {
        actionName,
        companyIds,
        ...payload,
      },
    })

    return data
  } catch (e) {
    SfLog.info('[Insight triggers] Failed to fetch non actionable Companies', {
      extra: {
        ...window.Silverfin.jsonParams,
        error: e,
      },
    })

    return []
  }
}

// Account lists for the template-starring details drawer. Throws on failure
// so the drawer store can show its error state.
const fetchTemplateStarringAccounts = async ({
  ledgerId,
  accountDetailTemplateId,
}) => {
  const url =
    Silverfin.Routes.template_starring_accounts_companies_overview_index_path(
      Silverfin.currentFirmId,
      {
        ledger_id: ledgerId,
        account_detail_template_id: accountDetailTemplateId,
      }
    )

  return SFetch.json(url, undefined, { camelizeJSON: false })
}

const fetchFilterDropdownValues = async () => {
  const firmId = Silverfin.currentFirmId
  const filterDropdownValuesEndpoint =
    Silverfin.Routes.filter_dropdown_values_companies_overview_index_path(
      firmId
    )

  try {
    const data = await SFetch.json(filterDropdownValuesEndpoint, undefined, {
      camelizeJSON: false,
    })

    return data
  } catch {
    SfLog.warn(
      `Cannot load filter dropdown values for Firm ${Silverfin.currentFirmId}`
    )

    return {
      company_templates: [],
      currencies: [],
      countries: [],
      company_types: [],
      sync_entities: [],
      sync_types: [],
      sync_references: [],
      administration_sync_entities: [],
      administration_sync_types: [],
      administration_sync_references: [],
      report_frequencies: [],
      firm_workflows: [],
      workflow_statuses: [],
      account_mapping_lists: [],
      account_detail_templates: [],
      groups: [],
    }
  }
}

const searchSyncEndpoint = async (routeBuilder, query) => {
  const firmId = Silverfin.currentFirmId
  const url = routeBuilder(firmId)

  try {
    return await SFetch.json(
      `${url}?query=${encodeURIComponent(query)}`,
      undefined,
      { camelizeJSON: false }
    )
  } catch (e) {
    SfLog.error('Failed to search sync endpoint', {
      extra: {
        ...window.Silverfin.jsonParams,
        url,
        error: e,
      },
    })

    return []
  }
}

const searchSyncEntities = (query) =>
  searchSyncEndpoint(
    Silverfin.Routes.search_sync_entities_companies_overview_index_path,
    query
  )

const searchSyncReferences = (query) =>
  searchSyncEndpoint(
    Silverfin.Routes.search_sync_references_companies_overview_index_path,
    query
  )

const searchAdministrationSyncEntities = (query) =>
  searchSyncEndpoint(
    Silverfin.Routes
      .search_administration_sync_entities_companies_overview_index_path,
    query
  )

const searchAdministrationSyncReferences = (query) =>
  searchSyncEndpoint(
    Silverfin.Routes
      .search_administration_sync_references_companies_overview_index_path,
    query
  )

const fetchReconciliationTexts = async () => {
  const url = Silverfin.Routes.reconciliation_texts_path(
    Silverfin.currentFirmId
  )

  try {
    const data = await SFetch.json(url, undefined, {
      camelizeJSON: false,
    })

    return data
  } catch (e) {
    SfLog.info('Failed to load insights reconciliation texts', {
      extra: {
        ...window.Silverfin.jsonParams,
        error: e,
      },
    })

    return []
  }
}

const fetchSegment = async ({ segmentId, partnerSegmentId }) => {
  const firmId = Silverfin.currentFirmId
  const segmentEndpoint = Silverfin.Routes.segment_path(firmId, segmentId)

  const url = `${segmentEndpoint}${
    partnerSegmentId
      ? '?' +
        new URLSearchParams({
          partner_segment_id: partnerSegmentId,
        })
      : ''
  }`

  try {
    const data = await SFetch.json(url, undefined, {
      camelizeJSON: false,
    })

    return data
  } catch {
    SfLog.warn(`Cannot load Segment with ID ${segmentId} for Firm ${firmId}`)

    return {}
  }
}

const fetchSegments = async () => {
  const firmId = Silverfin.currentFirmId
  const segmentsEndpoint = Silverfin.Routes.segments_path(firmId)

  try {
    const data = await SFetch.json(segmentsEndpoint, undefined, {
      camelizeJSON: false,
    })

    return data
  } catch (e) {
    SfLog.info('Failed to load insights segments', {
      extra: {
        ...window.Silverfin.jsonParams,
        error: e,
      },
    })

    return []
  }
}

const runTrigger = async ({ triggerId, segmentId }) => {
  const url = Silverfin.Routes.run_insight_trigger_path(
    Silverfin.currentFirmId,
    triggerId,
    { segment_id: segmentId }
  )

  try {
    return await SFetch.json(url, { method: 'POST' }, { camelizeJSON: false })
  } catch (e) {
    SfLog.info('[Insight triggers] Failed to run trigger', {
      extra: {
        ...window.Silverfin.jsonParams,
        error: e,
      },
    })

    return null
  }
}

const fetchTriggers = async (segmentId) => {
  const url = Silverfin.Routes.insight_triggers_path({
    firm_id: Silverfin.currentFirmId,
    segment_id: segmentId,
  })

  try {
    const data = await SFetch.json(url, undefined, {
      camelizeJSON: false,
    })

    return data
  } catch {
    SfLog.warn(
      `Cannot load Triggers with Segment ID ${segmentId} for Firm ${Silverfin.currentFirmId}`
    )

    return []
  }
}

const fetchUsers = async () => {
  const firmId = Silverfin.currentFirmId
  const usersEndpoint =
    Silverfin.Routes.users_companies_overview_index_path(firmId)

  try {
    const data = await SFetch.json(usersEndpoint, undefined, {
      camelizeJSON: false,
    })

    return data
  } catch {
    SfLog.warn(`Cannot load Users for Firm ${Silverfin.currentFirmId}`)

    return {
      users: [],
    }
  }
}

const fetchSubmissionDropdownData = async () => {
  const firmId = Silverfin.currentFirmId
  const url =
    Silverfin.Routes.submissions_dropdown_values_companies_overview_index_path(
      firmId
    )

  try {
    const data = await SFetch.json(url, undefined, { camelizeJSON: false })
    return data
  } catch {
    SfLog.warn(`Cannot load Submission Dropdown Data for Firm ${firmId}`)

    return { submissionData: [] }
  }
}

export default {
  fetchChunkResults({ requestRef, chunkIndex, signal }) {
    const firmId = Silverfin.currentFirmId
    const baseUrl =
      Silverfin.Routes.chunk_results_companies_overview_index_path(firmId)
    const urlParams = new URLSearchParams({
      request_ref: requestRef,
      chunk_index: chunkIndex,
    })

    return SFetch.json(
      `${baseUrl}?${urlParams}`,
      { method: 'GET', signal },
      { camelizeJSON: false }
    )
  },
  fetchFilteredCompanies({
    rootState,
    activeFilters,
    allFiltersByType,
    segmentId,
    partnerSegmentId,
    requestRef,
    pendingRequestRef,
    noCache,
    signal,
  }) {
    const firmId = Silverfin.currentFirmId
    const url = Silverfin.Routes.filter_companies_overview_index_path(firmId)

    return SFetch.json(url, {
      method: 'POST',
      signal,
      body: {
        ledger_select: serialize.ledgerSelectSettings(rootState),
        filters: serialize.filterSettings(activeFilters, allFiltersByType),
        request_ref: requestRef,
        pending_request_ref: pendingRequestRef,
        segment_id: segmentId,
        partner_segment_id: partnerSegmentId,
        no_cache: noCache,
      },
    })
  },
  fetchFilteredCompaniesRetry({
    rootState,
    activeFilters,
    allFiltersByType,
    requestRef,
    pendingRequestRef,
    missingCompanyIds,
    signal,
  }) {
    const firmId = Silverfin.currentFirmId
    const url =
      Silverfin.Routes.filter_retry_companies_overview_index_path(firmId)

    return SFetch.json(url, {
      method: 'POST',
      signal,
      body: {
        ledger_select: serialize.ledgerSelectSettings(rootState),
        filters: serialize.filterSettings(activeFilters, allFiltersByType),
        request_ref: requestRef,
        pending_request_ref: pendingRequestRef,
        missing_company_ids: missingCompanyIds,
      },
    })
  },
  fetchFilterDropdownValues,
  fetchTemplateStarringAccounts,
  searchSyncEntities,
  searchSyncReferences,
  searchAdministrationSyncEntities,
  searchAdministrationSyncReferences,
  fetchReconciliationTexts,
  fetchSegment,
  fetchSegments,
  fetchTriggers,
  runTrigger,
  fetchUsers,
  fetchSubmissionDropdownData,
  runManualAction,
  fetchNonActionableCompanies,
  createAgentConversation(message, context) {
    const url = Silverfin.Routes.agent_conversations_path(
      Silverfin.currentFirmId
    )

    // Keep the response snake_case: the store reads message_bus_last_id and the
    // reconcile poll reads latest_result, and the terminal payload is handed to
    // handleAgentPayload in the same snake_case shape MessageBus delivers.
    return SFetch.json(
      url,
      { method: 'POST', body: { message, context } },
      { camelizeJSON: false }
    )
  },
  sendAgentMessage(conversationId, message, context) {
    const url = Silverfin.Routes.messages_agent_conversation_path(
      Silverfin.currentFirmId,
      conversationId
    )

    return SFetch.json(
      url,
      { method: 'POST', body: { message, context } },
      { camelizeJSON: false }
    )
  },
  fetchAgentConversation(conversationId) {
    const url = Silverfin.Routes.agent_conversation_path(
      Silverfin.currentFirmId,
      conversationId
    )

    return SFetch.json(url, { method: 'GET' }, { camelizeJSON: false })
  },
  fetchAgentSuggestedPrompts() {
    const url = Silverfin.Routes.suggested_prompts_agent_conversations_path(
      Silverfin.currentFirmId
    )

    // Un-camelized like the other agent endpoints: the response carries message_bus_channel
    // and message_bus_last_id, which the store passes straight to the MessageBus client.
    return SFetch.json(url, { method: 'GET' }, { camelizeJSON: false })
  },

  cancelPendingRequest({ pending_request_ref }) {
    const firmId = Silverfin.currentFirmId
    const url =
      Silverfin.Routes.cancel_pending_request_companies_overview_index_path(
        firmId
      )
    const body = JSON.stringify({
      companies_overview: { pending_request_ref },
    })
    navigator.sendBeacon(url, new Blob([body], { type: 'application/json' }))
  },
}
